import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import {
	addNewEmailRecipient,
	deleteEmailRecipient,
	deleteNickname,
	getBeneficiaries,
	getDocumentDeliveryPreference,
	getEmailRecipients,
	updateDocumentDeliveryPreference,
	updateEmailRecipient,
	updateNickname
} from '@api/settings';
import { BankAccount, NewManualBankAccount } from 'types/payment';
import {
	CheckoutAccountFormStatus,
	EntityAccountDetails,
	JointAccountDetails,
	TrustAccountDetails
} from 'types/checkout';
import { DocumentDeliveryPreferenceEnum, EmailRecipient } from 'types/settings';
import {
	DripStatus,
	InvestmentEntity,
	InvestmentEntityType,
	IraEntityMetadata,
	UserReferralEligibilityStatus
} from 'types/investment-entity';
import { getBankAccounts, linkManualBankAccount } from '@api/bank-accounts';
import {
	getEntityAccountDetails,
	getJointAccountDetails,
	getTrustAccountDetails,
	isEligibleForAdditionalInvestment,
	updateInvestmentEntityInSession
} from '@api/checkout';
import { getNotifications, updateNotifications } from '@api/header';
import { getPerformanceData, getPositionsTable } from '@api/investment-entity';
import { getReitElementsForEntity, getReitElementsForPlan } from '@api/reit-element';
import { AdditionalInvestmentEligibility } from 'types/add-funds';
import { AutoInvestSchedule } from 'types/auto-invest';
import { Beneficiary } from 'types/account';
import { deleteRecurringInvestmentSchedule } from '@api/auto-invest';
import { getDrip } from '@api/drip';
import { getIraAccountDetails } from '@src/api/ira';
import { getRecurringInvestmentSchedule } from '@api/auto-invest';
import { InvestmentStrategy } from 'types/plan';
import { IraAccountDetails } from 'types/ira';
import { Link } from 'types/layout';
import { NotificationResponse } from 'types/header';
import { Performance } from 'types/performance';
import { Positions } from 'types/portfolio';
import ReitElement from '@classes/ReitElement';
import { RiaPlan } from 'types/plan';
import { ServiceLevelAssignment } from 'types/service-level';
import store from '..';
import { updateRiaPlan } from '@api/investment-entity';
import { updateUserInSession } from '@api/user';
import { user } from '@src/store/modules/user';
import Vue from 'vue';

function accountDetailsRoute(entityType: InvestmentEntityType, userRoute: CheckoutAccountFormStatus): Link {
	switch (entityType) {
		case 'INDIVIDUAL':
			return { router: 'checkout-individual-account-details' };
		case 'JOINT_ACCOUNT':
			return { router: 'checkout-joint-account-details', query: { account: userRoute } };
		case 'LLC':
			return {
				router: 'checkout-entity-account-details',
				query: { account: userRoute }
			};
		case 'TRUST':
			return { router: 'checkout-trust-account-details', query: { account: userRoute } };
		default:
			return { router: '404' };
	}
}

@Module({
	dynamic: true,
	namespaced: true,
	name: 'investmentEntity',
	store
})
class InvestmentEntityModule extends VuexModule {
	additionalInvestmentEligibility: AdditionalInvestmentEligibility = 'ENTITY_MISSING_INFORMATION';
	beneficiaries: Array<Beneficiary> = [];
	currentRiaPlanName = '';
	currentRiaPlanInvestmentStrategy: InvestmentStrategy | null = null;
	currentSelectedRiaPlanId = '';
	currentServiceLevel: ServiceLevelAssignment = {
		label: '',
		name: '',
		qualificationStatus: 'ERROR'
	};
	documentDeliveryPreference: DocumentDeliveryPreferenceEnum | null = null;
	eligibleForDrip = false;
	eligibleForInvestorGoal = false;
	eligibleForRecurringInvestment = false;
	entityType: InvestmentEntityType = 'INDIVIDUAL';
	entityTypeLabel = '';
	dripEnabled = false;
	dripPreference: DripStatus | string = '';
	freezeReason = '';
	hasActiveRecurringInvestmentSchedule = false;
	hasEverOwnedIpoShares = false;
	hasLegacyInvestments = false;
	investmentEntityId = '';
	investmentEntityName = '';
	investorGoalExists = false;
	iraEntityMetadata: IraEntityMetadata = {
		isMtcIra: false,
		isIraOnboardingFinished: false,
		iraType: null,
		iraTypeLabel: ''
	};
	isFrozen = false;
	isIraEntity = false; // includes IRA entities and LLC entities with a tax-designation of IRA
	isReitInvestor = false;
	isVerifiedAccredited = false;
	nickname = '';
	showBrowseMenuItem = false;
	showTaxCenter = false;
	userReferralEligibilityStatus: UserReferralEligibilityStatus = 'INELIGIBLE';

	// Auto-populated on load
	reitElements: Array<ReitElement> | null = null;
	notifications: NotificationResponse = {
		notifications: [],
		generatedTimestamp: '',
		lastNotificationView: ''
	};

	// Populated on request
	additionalEmailRecipients: Array<EmailRecipient> = [];
	autoInvestSchedule: AutoInvestSchedule | null = null;
	bankAccounts: Array<BankAccount> = [];
	entityAccountDetails: EntityAccountDetails | null = null;
	iraAccountDetails: IraAccountDetails | null = null;
	jointAccountDetails: JointAccountDetails | null = null;
	trustAccountDetails: TrustAccountDetails | null = null;
	positions: Positions | null = null;
	performance: Performance | null = null;

	get investmentEntityObject(): InvestmentEntity {
		return {
			additionalInvestmentEligibility: this.additionalInvestmentEligibility,
			currentRiaPlanName: this.currentRiaPlanName,
			currentRiaPlanInvestmentStrategy: this.currentRiaPlanInvestmentStrategy,
			currentSelectedRiaPlanId: this.currentSelectedRiaPlanId,
			currentServiceLevel: this.currentServiceLevel,
			dripEnabled: this.dripEnabled,
			eligibleForDrip: this.eligibleForDrip,
			eligibleForInvestorGoal: this.eligibleForInvestorGoal,
			eligibleForRecurringInvestment: this.eligibleForRecurringInvestment,
			entityType: this.entityType,
			entityTypeLabel: this.entityTypeLabel,
			freezeReason: this.freezeReason,
			hasActiveRecurringInvestmentSchedule: this.hasActiveRecurringInvestmentSchedule,
			hasEverOwnedIpoShares: this.hasEverOwnedIpoShares,
			hasLegacyInvestments: this.hasLegacyInvestments,
			investmentEntityId: this.investmentEntityId,
			investmentEntityName: this.investmentEntityName,
			investorGoalExists: this.investorGoalExists,
			iraEntityMetadata: this.iraEntityMetadata,
			isFrozen: this.isFrozen,
			isIraEntity: this.isIraEntity,
			isReitInvestor: this.isReitInvestor,
			isVerifiedAccredited: this.isVerifiedAccredited,
			nickname: this.nickname,
			showBrowseMenuItem: this.showBrowseMenuItem,
			showTaxCenter: this.showTaxCenter,
			userReferralEligibilityStatus: this.userReferralEligibilityStatus
		};
	}

	get isLegacyInvestorWithCashDistribution(): boolean {
		return !this.currentSelectedRiaPlanId && !this.dripEnabled;
	}

	get canUpdateInvestmentPlan(): boolean {
		const validServiceLevels = ['CORE', 'ADVANCED', 'PREMIUM'];
		return this.currentSelectedRiaPlanId !== '' && validServiceLevels.includes(this.currentServiceLevel.name);
	}

	get hasNeverBeenAnInvestor(): boolean {
		return !this.isReitInvestor && !this.hasLegacyInvestments;
	}

	@Action({ rawError: true })
	public setInvestmentEntity(newEntity: InvestmentEntity): void {
		this.UPDATE_ADDITIONAL_INVESTMENT_ELIGIBILITY(newEntity.additionalInvestmentEligibility);
		this.UPDATE_CURRENT_RIA_PLAN_NAME(newEntity?.currentRiaPlanName ?? '');
		this.UPDATE_CURRENT_RIA_PLAN_INVESTMENT_STRATEGY(newEntity?.currentRiaPlanInvestmentStrategy ?? null);
		this.UPDATE_CURRENT_SELECTED_RIA_PLAN_ID(newEntity?.currentSelectedRiaPlanId ?? '');
		this.UPDATE_CURRENT_SERVICE_LEVEL(newEntity.currentServiceLevel);
		this.UPDATE_DRIP_ENABLED(newEntity.dripEnabled);
		this.UPDATE_ELIGIBLE_FOR_DRIP(newEntity.eligibleForDrip);
		this.UPDATE_ELIGIBLE_FOR_INVESTOR_GOAL(newEntity.eligibleForInvestorGoal);
		this.UPDATE_ELIGIBLE_FOR_RECURRING_INVESTMENT(newEntity.eligibleForRecurringInvestment);
		this.UPDATE_ENTITY_TYPE(newEntity.entityType);
		this.UPDATE_ENTITY_TYPE_LABEL(newEntity?.entityTypeLabel ?? '');
		this.UPDATE_FREEZE_REASON(newEntity?.freezeReason ?? '');
		this.UPDATE_HAS_ACTIVE_RECURRING_INVESTMENT_SCHEDULE(newEntity.hasActiveRecurringInvestmentSchedule);
		this.UPDATE_HAS_EVER_OWNED_IPO_SHARES(newEntity.hasEverOwnedIpoShares);
		this.UPDATE_HAS_LEGACY_INVESTMENTS(newEntity.hasLegacyInvestments);
		this.UPDATE_INVESTMENT_ENTITY_ID(newEntity.investmentEntityId);
		this.UPDATE_INVESTMENT_ENTITY_NAME(newEntity.investmentEntityName);
		this.UPDATE_INVESTOR_GOAL_EXISTS(newEntity.investorGoalExists);
		this.UPDATE_IRA_ENTITY_METADATA(newEntity.iraEntityMetadata);
		this.UPDATE_IS_FROZEN(newEntity.isFrozen);
		this.UPDATE_IS_IRA_ENTITY(newEntity.isIraEntity);
		this.UPDATE_IS_REIT_INVESTOR(newEntity.isReitInvestor);
		this.UPDATE_IS_VERIFIED_ACCREDITIED(newEntity.isVerifiedAccredited);
		this.UPDATE_NICKNAME(newEntity.nickname);
		this.UPDATE_SHOW_BROWSE_MENU_ITEM(newEntity.showBrowseMenuItem);
		this.UPDATE_SHOW_TAX_CENTER(newEntity.showTaxCenter);
		this.UPDATE_USER_REFERRAL_ELIGIBILITY_STATUS(newEntity.userReferralEligibilityStatus);
		this.storeReitElements();
		this.storeNotifications();
	}

	@Action({ rawError: true })
	public async storeReitElements(): Promise<void> {
		const reitElements = await getReitElementsForEntity();
		this.UPDATE_REIT_ELEMENTS(reitElements);
	}

	@Action({ rawError: true })
	public async storeReitElementsForPlan(planId: string): Promise<void> {
		const reitElements = await getReitElementsForPlan(planId);
		this.UPDATE_REIT_ELEMENTS(reitElements);
	}

	@Action({ rawError: true })
	public async storeAutoInvestSchedule(): Promise<void> {
		if (this.autoInvestSchedule === null) {
			const autoInvestSchedule = await getRecurringInvestmentSchedule();
			this.UPDATE_AUTO_INVEST_SCHEDULE(autoInvestSchedule);
		}
	}

	@Action({ rawError: true })
	public async storeBankAccounts(): Promise<void> {
		if (this.bankAccounts.length === 0) {
			const bankAccounts = await getBankAccounts();
			this.UPDATE_BANK_ACCOUNTS(bankAccounts);
		}
	}

	@Action({ rawError: true })
	public async storeBeneficiaries(force?: boolean): Promise<void> {
		if (this.beneficiaries.length === 0 || force) {
			const beneficiaries = await getBeneficiaries();
			this.UPDATE_BENEFICIARIES(beneficiaries);
		}
	}

	@Action({ rawError: true })
	public async storeEmailRecipients(force?: boolean): Promise<void> {
		if (this.additionalEmailRecipients.length === 0 || force) {
			const recipients = await getEmailRecipients();
			this.UPDATE_ADDITIONAL_EMAIL_RECIPIENTS(recipients);
		}
	}

	@Action
	public async storeEntityAccountDetails(force = false): Promise<void> {
		if (!this.entityAccountDetails || force) {
			const entityAccountDetails = await getEntityAccountDetails(investmentEntity.investmentEntityId);
			this.UPDATE_ENTITY_ACCOUNT_DETAILS(entityAccountDetails);
		}
	}

	@Action({ rawError: true })
	public async storeIraAccountDetails(): Promise<void> {
		if (this.entityType === 'IRA' && this.iraAccountDetails === null) {
			const iraAccountDetails = await getIraAccountDetails();
			this.UPDATE_IRA_ACCOUNT_DETAILS(iraAccountDetails);
		}
	}

	@Action({ rawError: true })
	public async storeJointAccountDetails(force = false): Promise<void> {
		if (!this.jointAccountDetails || force) {
			const jointAccountDetails = await getJointAccountDetails();
			this.UPDATE_JOINT_ACCOUNT_DETAILS(jointAccountDetails);
		}
	}

	@Action({ rawError: true })
	public async storeNotifications(): Promise<void> {
		if (this.notifications.notifications.length === 0 && !user.user?.isThirdPartyLogin) {
			const notifications = await getNotifications();
			this.UPDATE_NOTIFICATIONS(notifications);
		}
	}

	@Action({ rawError: true })
	public async updateNotificationsView(): Promise<void> {
		const timestamp = this.notifications.generatedTimestamp;
		await updateNotifications({ timestamp });
		this.UPDATE_NOTIFICATIONS_LAST_VIEWED(timestamp);
	}

	@Action({ rawError: true })
	public async storePositions(): Promise<void> {
		if (this.positions === null) {
			const positions = await getPositionsTable();
			this.UPDATE_POSITIONS(positions);
		}
	}

	@Action({ rawError: true })
	public async refreshPositionsAndPerformance(): Promise<void> {
		const positions = await getPositionsTable();
		this.UPDATE_POSITIONS(positions);

		const performance = await getPerformanceData();
		this.UPDATE_PERFORMANCE(performance);
	}

	@Action({ rawError: true })
	public async storePerformance(): Promise<void> {
		if (this.performance === null) {
			const performance = await getPerformanceData();
			this.UPDATE_PERFORMANCE(performance);
		}
	}

	@Action
	public async storeTrustAccountDetails(force = false): Promise<void> {
		if (!this.trustAccountDetails || force) {
			const trustAccountDetailsData = await getTrustAccountDetails();
			this.UPDATE_TRUST_ACCOUNT_DETAILS(trustAccountDetailsData);
		}
	}

	@Action({ rawError: true })
	public async storeDripPreference(): Promise<void> {
		if (this.dripPreference === '') {
			const dripPreference = await getDrip();
			this.UPDATE_DRIP_PREFERENCE(dripPreference);
		}
	}

	@Action({ rawError: true })
	public async storeDocumentDeliveryPreference(): Promise<void> {
		if (this.documentDeliveryPreference === null) {
			const preference = await getDocumentDeliveryPreference();
			this.UPDATE_DOCUMENT_DELIVERY_PREFERENCE(preference);
		}
	}

	@Action({ rawError: true })
	public async updateDocumentDeliveryPreference(preference: DocumentDeliveryPreferenceEnum): Promise<void> {
		await updateDocumentDeliveryPreference(preference);
		await user.getUser();
		this.UPDATE_DOCUMENT_DELIVERY_PREFERENCE(preference);
	}

	@Action({ rawError: true })
	public async submitManualBankAccount(payload: NewManualBankAccount): Promise<BankAccount> {
		const linkedAccount = await linkManualBankAccount(payload);
		this.ADD_BANK_ACCOUNT(linkedAccount);
		return linkedAccount;
	}

	@Action({ rawError: true })
	private async updateUserAndEmailRecipients(): Promise<void> {
		await user.getUser();
		await Promise.all([updateUserInSession(), this.storeEmailRecipients(true)]);
	}

	@Action({ rawError: true })
	public async addOrUpdateEmailRecipient(payload: { recipientId: string; details: EmailRecipient }): Promise<void> {
		if (payload.recipientId) {
			await updateEmailRecipient(payload.recipientId, payload.details);
		} else {
			await addNewEmailRecipient(payload.details);
		}
		await this.updateUserAndEmailRecipients();
	}

	@Action({ rawError: true })
	public async deleteEmailRecipient(recipientId: string): Promise<void> {
		await deleteEmailRecipient(recipientId);
		await this.updateUserAndEmailRecipients();
	}

	@Action({ rawError: true })
	public async updateExistingInvestmentEntity(investmentEntity: InvestmentEntity): Promise<Link> {
		const { investmentEntityId, entityType } = investmentEntity;
		this.setInvestmentEntity(investmentEntity);
		await updateInvestmentEntityInSession(investmentEntityId);
		const isRepeatInvestor = await isEligibleForAdditionalInvestment(investmentEntityId);
		if (isRepeatInvestor) {
			return {
				href: '/account/additional-investment/start'
			};
		} else {
			return accountDetailsRoute(entityType, 'update');
		}
	}

	@Action({ rawError: true })
	public async createNewInvestmentEntity(entityType: InvestmentEntityType): Promise<Link> {
		return accountDetailsRoute(entityType, 'create');
	}

	@Action({ rawError: true })
	public async updateRiaPlan(plan: RiaPlan): Promise<void> {
		await updateRiaPlan(plan.id);

		this.UPDATE_CURRENT_SELECTED_RIA_PLAN_ID(plan.id);
		this.UPDATE_CURRENT_RIA_PLAN_NAME(plan.name);
		this.UPDATE_CURRENT_RIA_PLAN_INVESTMENT_STRATEGY(plan.investmentStrategy);
	}

	@Action({ rawError: true })
	public async updateNickname(newNickname: string): Promise<void> {
		this.UPDATE_NICKNAME(newNickname);
		await updateNickname(newNickname);
	}

	@Action({ rawError: true })
	public async deleteNickname(): Promise<void> {
		this.UPDATE_NICKNAME(null);
		await deleteNickname();
	}

	@Action({ rawError: true })
	public async deleteRecurringInvestmentSchedule(reminderSet: boolean): Promise<void> {
		await deleteRecurringInvestmentSchedule(reminderSet);
		this.UPDATE_AUTO_INVEST_SCHEDULE(null);
		this.UPDATE_HAS_ACTIVE_RECURRING_INVESTMENT_SCHEDULE(false);
	}

	@Mutation
	public UPDATE_ADDITIONAL_INVESTMENT_ELIGIBILITY(eligibility: AdditionalInvestmentEligibility): void {
		this.additionalInvestmentEligibility = eligibility;
	}

	@Mutation
	public UPDATE_AUTO_INVEST_SCHEDULE(autoInvestSchedule: AutoInvestSchedule | null): void {
		this.autoInvestSchedule = autoInvestSchedule;
	}

	@Mutation
	public UPDATE_BANK_ACCOUNTS(bankAccounts: BankAccount[]): void {
		this.bankAccounts = bankAccounts;
	}

	@Mutation
	public UPDATE_BENEFICIARIES(beneficiaries: Array<Beneficiary>): void {
		this.beneficiaries = beneficiaries;
	}

	@Mutation
	public ADD_BANK_ACCOUNT(bankAccount: BankAccount): void {
		this.bankAccounts.push(bankAccount);
	}

	@Mutation
	public UPDATE_ENTITY_ACCOUNT_DETAILS(entityAccountDetails: EntityAccountDetails): void {
		this.entityAccountDetails = entityAccountDetails;
	}

	@Mutation
	public UPDATE_IRA_ACCOUNT_DETAILS(iraAccountDetails: IraAccountDetails): void {
		this.iraAccountDetails = iraAccountDetails;
	}

	@Mutation
	public UPDATE_NOTIFICATIONS(notifications: NotificationResponse): void {
		this.notifications = notifications;
	}

	@Mutation
	public UPDATE_NOTIFICATIONS_LAST_VIEWED(timestamp: string): void {
		Vue.set(this.notifications, 'lastNotificationView', timestamp);
	}

	@Mutation
	public UPDATE_POSITIONS(positions: Positions): void {
		this.positions = positions;
	}

	@Mutation
	public UPDATE_PERFORMANCE(performance: Performance): void {
		this.performance = performance;
	}

	@Mutation
	public UPDATE_REIT_ELEMENTS(elements: Array<ReitElement>): void {
		this.reitElements = elements;
	}

	@Mutation
	public UPDATE_CURRENT_RIA_PLAN_NAME(name: string): void {
		this.currentRiaPlanName = name;
	}

	@Mutation
	public UPDATE_CURRENT_RIA_PLAN_INVESTMENT_STRATEGY(strategy: InvestmentStrategy | null): void {
		this.currentRiaPlanInvestmentStrategy = strategy;
	}

	@Mutation
	public UPDATE_CURRENT_SELECTED_RIA_PLAN_ID(name: string): void {
		this.currentSelectedRiaPlanId = name;
	}

	@Mutation
	public UPDATE_CURRENT_SERVICE_LEVEL(sl: ServiceLevelAssignment): void {
		this.currentServiceLevel = sl;
	}

	@Mutation
	public UPDATE_DOCUMENT_DELIVERY_PREFERENCE(preference: DocumentDeliveryPreferenceEnum): void {
		this.documentDeliveryPreference = preference;
	}

	@Mutation
	public UPDATE_DRIP_ENABLED(dripEnabled: boolean): void {
		this.dripEnabled = dripEnabled;
	}

	@Mutation
	public UPDATE_DRIP_PREFERENCE(dripPreference: DripStatus): void {
		this.dripPreference = dripPreference;
	}

	@Mutation
	public UPDATE_ELIGIBLE_FOR_DRIP(eligibleForDrip: boolean): void {
		this.eligibleForDrip = eligibleForDrip;
	}

	@Mutation
	public UPDATE_ELIGIBLE_FOR_INVESTOR_GOAL(eligible: boolean): void {
		this.eligibleForInvestorGoal = eligible;
	}

	@Mutation
	public UPDATE_ELIGIBLE_FOR_RECURRING_INVESTMENT(eligibleForRecurringInvestment: boolean): void {
		this.eligibleForRecurringInvestment = eligibleForRecurringInvestment;
	}

	@Mutation
	public UPDATE_ENTITY_TYPE(entityType: InvestmentEntityType): void {
		this.entityType = entityType;
	}

	@Mutation
	public UPDATE_ENTITY_TYPE_LABEL(label: string): void {
		this.entityTypeLabel = label;
	}

	@Mutation
	public UPDATE_FREEZE_REASON(freezeReason: string): void {
		this.freezeReason = freezeReason;
	}

	@Mutation
	public UPDATE_HAS_EVER_OWNED_IPO_SHARES(hasEverOwnedIpoShares: boolean): void {
		this.hasEverOwnedIpoShares = hasEverOwnedIpoShares;
	}

	@Mutation
	public UPDATE_HAS_ACTIVE_RECURRING_INVESTMENT_SCHEDULE(hasActiveRecurringInvestmentSchedule: boolean): void {
		this.hasActiveRecurringInvestmentSchedule = hasActiveRecurringInvestmentSchedule;
	}

	@Mutation
	public UPDATE_HAS_LEGACY_INVESTMENTS(hasLegacyInvestments: boolean): void {
		this.hasLegacyInvestments = hasLegacyInvestments;
	}

	@Mutation
	public UPDATE_INVESTMENT_ENTITY_ID(id: string): void {
		this.investmentEntityId = id;
	}

	@Mutation
	public UPDATE_INVESTMENT_ENTITY_NAME(name: string): void {
		this.investmentEntityName = name;
	}

	@Mutation
	public UPDATE_INVESTOR_GOAL_EXISTS(investorGoalExists: boolean): void {
		this.investorGoalExists = investorGoalExists;
	}

	@Mutation
	public UPDATE_IRA_ENTITY_METADATA(iraEntityMetadata: IraEntityMetadata): void {
		this.iraEntityMetadata = iraEntityMetadata;
	}

	@Mutation
	public UPDATE_IS_FROZEN(isFrozen: boolean): void {
		this.isFrozen = isFrozen;
	}

	@Mutation
	public UPDATE_IS_IRA_ENTITY(isIra: boolean): void {
		this.isIraEntity = isIra;
	}

	@Mutation
	public UPDATE_IS_REIT_INVESTOR(isReitInvestor: boolean): void {
		this.isReitInvestor = isReitInvestor;
	}

	@Mutation
	public UPDATE_IS_VERIFIED_ACCREDITIED(acc: boolean): void {
		this.isVerifiedAccredited = acc;
	}

	@Mutation
	public UPDATE_JOINT_ACCOUNT_DETAILS(jointAccountDetails: JointAccountDetails): void {
		this.jointAccountDetails = jointAccountDetails;
	}

	@Mutation
	public UPDATE_NICKNAME(nickname: string | null): void {
		this.nickname = nickname ?? '';
	}

	@Mutation
	public UPDATE_SHOW_BROWSE_MENU_ITEM(showBrowseMenu: boolean): void {
		this.showBrowseMenuItem = showBrowseMenu;
	}

	@Mutation
	public UPDATE_SHOW_TAX_CENTER(showTaxCenter: boolean): void {
		this.showTaxCenter = showTaxCenter;
	}

	@Mutation
	public UPDATE_TRUST_ACCOUNT_DETAILS(trustAccountDetails: TrustAccountDetails): void {
		this.trustAccountDetails = trustAccountDetails;
	}

	@Mutation
	public UPDATE_USER_REFERRAL_ELIGIBILITY_STATUS(status: UserReferralEligibilityStatus): void {
		this.userReferralEligibilityStatus = status;
	}

	@Mutation
	public UPDATE_ADDITIONAL_EMAIL_RECIPIENTS(recipients: Array<EmailRecipient>): void {
		this.additionalEmailRecipients = recipients;
	}
}

export const investmentEntity = getModule(InvestmentEntityModule);
